import { Injectable } from '@angular/core';
import { CommonAPIService } from '../commonAPI.service';
import { Observable } from 'rxjs';
import { CommentSearchContent, FacetFilter, FieldFilter, SearchPayload, SearchResult } from '../../shared/models/search.model';
import { COMMENT_STATUS, FACET_LIST, FIELD_LIST } from '../../shared/constants/comment.constant';
import { MatSort } from '@angular/material/sort';
import { DomainMarketLang } from '../../shared/models/comments/comment.model';
import { CoupleIdLite } from '../../shared/models/generic/id/couple-id-lite.model';
import { CommentResponseType } from './comment-moderation.service';
import { ConfigService } from '../config.service';

@Injectable({
  providedIn: 'root',
})
export class CommentService extends CommonAPIService {
  getCommentList(
    pageIndex: number,
    pageSize: number,
    domainMarketLang: DomainMarketLang,
    q?: string,
    sort?: MatSort,
    facetFilters?: FacetFilter[],
    fieldFilters?: FieldFilter[]
  ): Observable<SearchResult<CommentSearchContent>> {
    const defaultFieldFilter = { field: 'status.key', values: COMMENT_STATUS };
    const payload: SearchPayload = {
      fieldList: FIELD_LIST,
      facetList: FACET_LIST,
      fieldFilters: [defaultFieldFilter, ...(fieldFilters ?? [])],
    };

    if (sort && sort.active && sort.direction) {
      payload.sort = {
        name: sort.active,
        direction: sort.direction,
      };
    }

    if (facetFilters && facetFilters.length > 0) {
      payload.facetFilters = facetFilters;
    }

    const searchParams = new URLSearchParams();
    searchParams.append('domain', domainMarketLang.domain);
    searchParams.append('lang', domainMarketLang.lang);
    searchParams.append('market', domainMarketLang.market);
    searchParams.append('page', pageIndex.toString());
    searchParams.append('size', pageSize.toString());

    if (q) {
      searchParams.append('q', q);
    }
    return this.post<SearchResult<CommentSearchContent>>('v4/comments/search?' + searchParams.toString(), payload);
  }

  batchApprove(comments: CommentSearchContent[]): Observable<CommentResponseType | null> {
    const updatedComments = comments.map((comment) => ({
      ...comment,
      status: 'PUBLISHED',
      reason: '',
    }));

    const payload = {
      commentLites: updatedComments,
    };

    return this.patch('v4/comments/batch', payload);
  }

  batchReject(comments: CommentSearchContent[], reason: string): Observable<CommentResponseType | null> {
    const updatedComments = comments.map((comment) => ({
      ...comment,
      status: 'REJECTED',
      reason,
    }));

    const payload = {
      commentLites: updatedComments,
      mailBody: '',
    };
    return this.patch('v4/comments/batch', payload);
  }

  batchDelete(comments: CommentSearchContent[]): Observable<CommentResponseType | null> {
    const requestBody: CoupleIdLite[] = comments.map((comment) => ({
      functionalId: comment.identifier.functionalId,
      sourceSystem: comment.identifier.sourceSystem,
    }));
    return this.delete('v4/comments/batch', undefined, requestBody);
  }

  initDomainMarketLang(configService: ConfigService): DomainMarketLang {
    const commentDomainMarketLang: DomainMarketLang = {
      domain: '',
      market: '',
      lang: '',
    };

    const defaultSearchQueryParams = configService.getDefaultSearchQueryParams();
    commentDomainMarketLang.domain = defaultSearchQueryParams.domain;
    commentDomainMarketLang.market = defaultSearchQueryParams.market;
    commentDomainMarketLang.lang = defaultSearchQueryParams.lang;

    return commentDomainMarketLang;
  }
}
